import { getAbsPath, sanitizeUrlParam, escapeHtml, getNewsUrl } from '../utils.js';
import { computeRelativeTime } from './core.js';
import { getFlagHtml, TEAM_FLAGS } from '../flags.js';

/**
 * Extrae el video ID de una URL de YouTube.
 * Soporta formatos: watch?v=, youtu.be/, shorts/, embed/
 */
function extractYouTubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') return u.pathname.slice(1).split('?')[0];
    if (u.hostname.includes('youtube.com')) {
      const v = u.searchParams.get('v');
      if (v) return v;
      const parts = u.pathname.split('/');
      const idx = parts.indexOf('shorts') !== -1 ? parts.indexOf('shorts') + 1
                : parts.indexOf('embed') !== -1 ? parts.indexOf('embed') + 1
                : -1;
      if (idx > -1) return parts[idx];
    }
  } catch (_) { /* URL inválida, ignorar */ }
  return null;
}

export function renderResults(results, fallbackDate = null, showFlags = false) {
  if (!Array.isArray(results)) return '';
  return results.map(match => {
    // Soporte para múltiples esquemas de nombres: homeTeam/awayTeam o home/away
    const teamHome = match.homeTeam || match.home;
    const teamAway = match.awayTeam || match.away;
    
    // Soporte para scores separados o unificados
    let scoreHome = match.homeScore;
    let scoreAway = match.awayScore;
    if (scoreHome === undefined && match.score && match.score.includes('-')) {
      const parts = match.score.split('-');
      scoreHome = parts[0].trim();
      scoreAway = parts[1].trim();
    }

    // Prioridad: fecha propia del partido → fecha calculada como fallback
    const matchDate = match.date || fallbackDate;
    const tournament = match.tournament || "General";

    // Highlights de video (opcional)
    const videoId = extractYouTubeId(match.highlightsUrl);
    const highlightsHtml = videoId ? `
      <button
        class="score-card__highlights-btn js-open-video"
        aria-label="Ver mejores jugadas de ${teamHome} vs ${teamAway}"
        data-video-id="${videoId}"
        data-video-title="${teamHome} vs ${teamAway} — Mejores Jugadas"
        type="button"
      >
        <div class="score-card__thumb-wrap">
          <img
            src="https://img.youtube.com/vi/${videoId}/mqdefault.jpg"
            alt="Highlights ${teamHome} vs ${teamAway}"
            class="score-card__thumb"
            loading="lazy"
            width="320" height="180"
          >
          <span class="score-card__play-icon"><i class="fas fa-play"></i></span>
        </div>
        <span class="score-card__highlights-label"><i class="fas fa-film"></i> Ver mejores jugadas</span>
      </button>
    ` : '';
    
    return `
    <div class="score-card fade-in">
      <div class="score-card__tournament">${tournament}</div>
      <div class="score-card__main">
        <div class="score-card__team">
          <span class="score-card__team-name">${showFlags ? getFlagHtml(teamHome) : ''} ${teamHome}</span>
        </div>
        <div class="score-card__result">
          <span class="score-card__goals ${parseInt(scoreHome) > parseInt(scoreAway) ? 'score-card__goals--winner' : ''}">${scoreHome}</span>
          <span class="score-card__divider">-</span>
          <span class="score-card__goals ${parseInt(scoreAway) > parseInt(scoreHome) ? 'score-card__goals--winner' : ''}">${scoreAway}</span>
        </div>
        <div class="score-card__team score-card__team--away">
          <span class="score-card__team-name">${teamAway} ${showFlags ? getFlagHtml(teamAway) : ''}</span>
        </div>
      </div>
      <div class="score-card__footer">
        <div class="score-card__status"><i class="fas fa-check-circle"></i> ${match.status}</div>
        ${matchDate ? `<div class="score-card__date"><i class="far fa-calendar-check"></i> ${matchDate}</div>` : ''}
      </div>
      ${highlightsHtml}
    </div>
  `;
  }).join('');
}

export function renderUpcoming(upcoming, showFlags = false) {
  if (!Array.isArray(upcoming)) return '';
  return upcoming.map(match => {
    const teamHome = match.homeTeam || match.home;
    const teamAway = match.awayTeam || match.away;
    const venue = match.venue || "A confirmar";
    const time = match.time || match.status || "--:--";

    return `
    <div class="score-card score-card--upcoming fade-in">
      <div class="score-card__tournament">${match.tournament || "Próximo"}</div>
      <div class="score-card__main">
        <div class="score-card__team">
          <span class="score-card__team-name">${showFlags ? getFlagHtml(teamHome) : ''} ${teamHome}</span>
        </div>
        <div class="score-card__vs">VS</div>
        <div class="score-card__team score-card__team--away">
          <span class="score-card__team-name">${teamAway} ${showFlags ? getFlagHtml(teamAway) : ''}</span>
        </div>
      </div>
      <div class="score-card__meta-info">
        <span class="${match.date === 'HOY' ? 'score-card__date--today' : ''}"><i class="far fa-calendar-alt"></i> ${match.date || 'Próximamente'}</span>
        <span><i class="far fa-clock"></i> ${time}</span>
        <span><i class="fas fa-location-dot"></i> ${venue}</span>
      </div>
    </div>
  `;
  }).join('');
}

export function renderWCNews(news, dateSuffix = '') {
  return news.map(item => `
    <article class="wc-card fade-in" data-news-category="Mundial" data-news-title="${item.title}">
      <div class="wc-card__img-wrap">
        <img src="${getAbsPath(item.image)}" 
             srcset="${getAbsPath(item.image).replace('.webp', '-600w.webp')} 600w, ${getAbsPath(item.image).replace('.webp', '-1200w.webp')} 1200w"
             sizes="(max-width: 600px) 100vw, 300px"
             alt="${item.title}" class="wc-card__img" loading="lazy" width="300" height="160" decoding="async">
      </div>
      <div class="wc-card__content">
        <h4 class="wc-card__title">${item.title}</h4>
        <p class="wc-card__excerpt">${item.excerpt}</p>
        <div class="wc-card__footer">
          <span><i class="far fa-clock"></i> ${computeRelativeTime(item.time, item.publishedAt, !!dateSuffix)}</span>
          <a href="${getNewsUrl(item.slug, dateSuffix)}" class="wc-card__link">Leer más <i class="fas fa-chevron-right"></i></a>
        </div>
      </div>
    </article>
  `).join('');
}
